import { create } from 'zustand';
import { temporal } from 'zundo';
import type { FormSchema, FormField, FormStep, ConditionalRule, FormSettings } from '../types/form';
import { generateUUID } from '../utils/uuid';

// Utility function to move item in array (replaces @dnd-kit arrayMove)
function arrayMove<T>(array: T[], from: number, to: number): T[] {
  // Validate indices
  if (from < 0 || from >= array.length || to < 0 || to >= array.length) {
    console.error('arrayMove: invalid indices', {
      from,
      to,
      arrayLength: array.length,
      stack: new Error().stack,
    });
    return array;
  }

  // Don't move if indices are the same
  if (from === to) {
    return array;
  }

  const newArray = [...array];
  const [removed] = newArray.splice(from, 1);
  newArray.splice(to, 0, removed);
  return newArray;
}

interface BuilderState {
  // Form data
  formId: string | null;
  formName: string;
  formSchema: FormSchema;
  originalSchema: FormSchema | null;

  // UI state
  selectedFieldId: string | null;
  selectedFieldIds: string[]; // For multi-select
  activeStepId: string | null;
  isDragging: boolean;
  hasUnsavedChanges: boolean;

  // Clipboard for copy/paste
  clipboard: FormField[] | null;

  // Actions
  initializeForm: (id: string, name: string, schema: FormSchema) => void;
  reset: () => void;

  // Field selection
  selectField: (fieldId: string | null) => void;

  // Multi-select operations
  toggleFieldSelection: (fieldId: string) => void;
  selectFieldRange: (fieldId: string) => void;
  selectAllFields: () => void;
  clearSelection: () => void;

  // Bulk operations
  removeSelectedFields: () => void;
  duplicateSelectedFields: () => void;
  moveSelectedFields: (direction: 'up' | 'down') => void;

  // Copy/paste
  copySelectedFields: () => void;
  pasteFields: () => void;

  // Field operations
  addField: (field: FormField, afterFieldId?: string) => void;
  updateField: (fieldId: string, updates: Partial<FormField>) => void;
  removeField: (fieldId: string) => void;
  reorderFields: (activeId: string, overId: string) => void;
  duplicateField: (fieldId: string) => void;
  moveFieldUp: (fieldId: string) => void;
  moveFieldDown: (fieldId: string) => void;

  // Layout column operations
  addFieldToLayoutColumn: (layoutFieldId: string, columnIndex: number, field: FormField, afterFieldId?: string) => void;
  removeFieldFromLayoutColumn: (layoutFieldId: string, columnIndex: number, fieldId: string) => void;
  reorderFieldsInLayoutColumn: (layoutFieldId: string, columnIndex: number, activeId: string, overId: string) => void;
  moveFieldFromCanvasToLayoutColumn: (fieldId: string, layoutFieldId: string, columnIndex: number) => void;
  moveFieldBetweenLayoutColumns: (fieldId: string, sourceLayoutId: string, sourceColumnIndex: number, targetLayoutId: string, targetColumnIndex: number) => void;

  // Repeatable column operations (same structure as layout columns)
  addFieldToRepeatableColumn: (repeatableFieldId: string, columnIndex: number, field: FormField, afterFieldId?: string) => void;
  removeFieldFromRepeatableColumn: (repeatableFieldId: string, columnIndex: number, fieldId: string) => void;
  reorderFieldsInRepeatableColumn: (repeatableFieldId: string, columnIndex: number, activeId: string, overId: string) => void;
  moveFieldFromCanvasToRepeatableColumn: (fieldId: string, repeatableFieldId: string, columnIndex: number) => void;
  moveFieldBetweenRepeatableColumns: (fieldId: string, sourceRepeatableId: string, sourceColumnIndex: number, targetRepeatableId: string, targetColumnIndex: number) => void;

  // Step operations
  addStep: (step: FormStep) => void;
  updateStep: (stepId: string, updates: Partial<FormStep>) => void;
  removeStep: (stepId: string) => void;
  reorderSteps: (activeId: string, overId: string) => void;
  moveFieldToStep: (fieldId: string, stepId: string) => void;

  // Conditional logic
  addCondition: (rule: ConditionalRule) => void;
  updateCondition: (ruleId: string, updates: Partial<ConditionalRule>) => void;
  removeCondition: (ruleId: string) => void;
  updateConditionalLogic: (rules: ConditionalRule[]) => void;

  // Settings
  updateSettings: (settings: Partial<FormSettings>) => void;
  updateQuizQuestions: (questions: Array<{
    id: string;
    question: string;
    options: Array<{ id: string; text: string; isCorrect: boolean }>;
    points: number;
  }>) => void;

  // Drag state
  setDragging: (isDragging: boolean) => void;

  // Get schema for saving
  getSchema: () => FormSchema;
  markAsSaved: () => void;
}

const initialSchema: FormSchema = {
  version: 1,
  fields: [],
  steps: [],
  settings: {
    submitButtonText: 'Submit',
    successMessage: 'Thank you for your submission!',
    theme: 'light',
    submitActions: [{ type: 'database' }],
  },
  conditionalLogic: [],
};

export const useBuilderStore = create<BuilderState>()(
  temporal(
    (set, get) => ({
  // Initial state
  formId: null,
  formName: '',
  formSchema: { ...initialSchema },
  originalSchema: null,
  selectedFieldId: null,
  selectedFieldIds: [],
  activeStepId: null,
  isDragging: false,
  hasUnsavedChanges: false,
  clipboard: null,

  initializeForm: (id, name, schema) => {
    const safeSchema = {
      ...schema,
      conditionalLogic: schema.conditionalLogic || [],
      steps: schema.steps || undefined,
    };
    set({
      formId: id,
      formName: name,
      formSchema: safeSchema,
      originalSchema: JSON.parse(JSON.stringify(safeSchema)),
      selectedFieldId: null,
      hasUnsavedChanges: false,
    });
  },

  reset: () =>
    set({
      formId: null,
      formName: '',
      formSchema: { ...initialSchema },
      originalSchema: null,
      selectedFieldId: null,
      selectedFieldIds: [],
      activeStepId: null,
      hasUnsavedChanges: false,
      clipboard: null,
    }),

  selectField: (fieldId) => set({ selectedFieldId: fieldId, selectedFieldIds: [] }),

  // Multi-select: toggle individual field (Ctrl+Click)
  toggleFieldSelection: (fieldId) =>
    set((state) => {
      const isSelected = state.selectedFieldIds.includes(fieldId);
      if (isSelected) {
        const newSelectedIds = state.selectedFieldIds.filter((id) => id !== fieldId);
        return {
          selectedFieldIds: newSelectedIds,
          selectedFieldId: newSelectedIds.includes(state.selectedFieldId!) ? state.selectedFieldId : null,
        };
      } else {
        return {
          selectedFieldIds: [...state.selectedFieldIds, fieldId],
          selectedFieldId: fieldId,
        };
      }
    }),

  // Multi-select: select range (Shift+Click)
  selectFieldRange: (fieldId) =>
    set((state) => {
      const fields = state.formSchema.fields;
      const lastSelected = state.selectedFieldId || state.selectedFieldIds[state.selectedFieldIds.length - 1];

      if (!lastSelected) {
        return { selectedFieldIds: [fieldId], selectedFieldId: fieldId };
      }

      const lastIndex = fields.findIndex((f) => f.id === lastSelected);
      const currentIndex = fields.findIndex((f) => f.id === fieldId);

      if (lastIndex === -1 || currentIndex === -1) {
        return { selectedFieldIds: [fieldId], selectedFieldId: fieldId };
      }

      const start = Math.min(lastIndex, currentIndex);
      const end = Math.max(lastIndex, currentIndex);
      const rangeIds = fields.slice(start, end + 1).map((f) => f.id);

      return {
        selectedFieldIds: [...new Set([...state.selectedFieldIds, ...rangeIds])],
        selectedFieldId: fieldId,
      };
    }),

  // Select all fields
  selectAllFields: () =>
    set((state) => ({
      selectedFieldIds: state.formSchema.fields.map((f) => f.id),
      selectedFieldId: state.formSchema.fields[0]?.id || null,
    })),

  // Clear multi-selection
  clearSelection: () => set({ selectedFieldIds: [], selectedFieldId: null }),

  // Remove all selected fields
  removeSelectedFields: () =>
    set((state) => {
      const idsToRemove = state.selectedFieldIds.length > 0
        ? state.selectedFieldIds
        : state.selectedFieldId
          ? [state.selectedFieldId]
          : [];

      if (idsToRemove.length === 0) return state;

      const fields = state.formSchema.fields.filter((f) => !idsToRemove.includes(f.id));
      const conditionalLogic = state.formSchema.conditionalLogic.filter(
        (r) => !idsToRemove.includes(r.targetFieldId) &&
               !r.conditions.some((c) => idsToRemove.includes(c.fieldId))
      );

      return {
        formSchema: { ...state.formSchema, fields, conditionalLogic },
        selectedFieldId: null,
        selectedFieldIds: [],
        hasUnsavedChanges: true,
      };
    }),

  // Duplicate all selected fields
  duplicateSelectedFields: () =>
    set((state) => {
      const idsToDuplicate = state.selectedFieldIds.length > 0
        ? state.selectedFieldIds
        : state.selectedFieldId
          ? [state.selectedFieldId]
          : [];

      if (idsToDuplicate.length === 0) return state;

      const fields = [...state.formSchema.fields];
      const newFieldIds: string[] = [];

      // Find the last selected field to insert after
      const indices = idsToDuplicate
        .map((id) => fields.findIndex((f) => f.id === id))
        .filter((i) => i !== -1);

      if (indices.length === 0) {
        console.warn('duplicateSelectedFields: No valid fields found', { idsToDuplicate });
        return state;
      }

      const lastIndex = Math.max(...indices);

      // Create copies of selected fields in order
      const fieldsToDuplicate: FormField[] = [];
      for (const id of idsToDuplicate) {
        const field = fields.find((f) => f.id === id);
        if (!field) {
          console.warn('duplicateSelectedFields: Field not found', { id });
          continue;
        }
        fieldsToDuplicate.push(field);
      }

      if (fieldsToDuplicate.length === 0) return state;

      const newFields = fieldsToDuplicate.map((field) => {
        const newId = generateUUID();
        newFieldIds.push(newId);
        return {
          ...JSON.parse(JSON.stringify(field)),
          id: newId,
          name: `${field.name}_copy`,
          label: `${field.label} (Copy)`,
        };
      });

      // Insert after the last selected field
      fields.splice(lastIndex + 1, 0, ...newFields);

      return {
        formSchema: { ...state.formSchema, fields },
        selectedFieldIds: newFieldIds,
        selectedFieldId: newFieldIds[0],
        hasUnsavedChanges: true,
      };
    }),

  // Move selected fields up or down
  moveSelectedFields: (direction) =>
    set((state) => {
      const idsToMove = state.selectedFieldIds.length > 0
        ? state.selectedFieldIds
        : state.selectedFieldId
          ? [state.selectedFieldId]
          : [];

      if (idsToMove.length === 0) return state;

      let fields = state.formSchema.fields;

      // Optimize: Use Map for O(1) lookups instead of O(n) findIndex
      const idToIndex = new Map(fields.map((f, i) => [f.id, i]));
      const indices = idsToMove
        .map(id => idToIndex.get(id))
        .filter((i): i is number => i !== undefined)
        .sort((a, b) => a - b);

      if (direction === 'up') {
        // Can't move up if first selected is at top
        if (indices[0] === 0) return state;

        // Use arrayMove for consistency with rest of codebase
        for (const index of indices) {
          if (index > 0) {
            fields = arrayMove(fields, index, index - 1);
          }
        }
      } else {
        // Can't move down if last selected is at bottom
        if (indices[indices.length - 1] === fields.length - 1) return state;

        // Move in reverse order to maintain correct indices
        for (let i = indices.length - 1; i >= 0; i--) {
          const index = indices[i];
          if (index < fields.length - 1) {
            fields = arrayMove(fields, index, index + 1);
          }
        }
      }

      return {
        formSchema: { ...state.formSchema, fields },
        hasUnsavedChanges: true,
      };
    }),

  // Copy selected fields to clipboard (also to localStorage for cross-form paste)
  copySelectedFields: () =>
    set((state) => {
      const idsToCopy = state.selectedFieldIds.length > 0
        ? state.selectedFieldIds
        : state.selectedFieldId
          ? [state.selectedFieldId]
          : [];

      if (idsToCopy.length === 0) return state;

      const fieldsToCopy = idsToCopy
        .map((id) => state.formSchema.fields.find((f) => f.id === id))
        .filter(Boolean) as FormField[];

      const clipboardData = JSON.parse(JSON.stringify(fieldsToCopy));

      // Store in localStorage for cross-form paste
      try {
        localStorage.setItem('tfasim_field_clipboard', JSON.stringify({
          fields: clipboardData,
          timestamp: Date.now(),
        }));
      } catch (e) {
        // Ignore localStorage errors
      }

      return { clipboard: clipboardData };
    }),

  // Paste fields from clipboard (checks localStorage for cross-form paste)
  pasteFields: () =>
    set((state) => {
      // Try in-memory clipboard first, then localStorage
      let clipboardData = state.clipboard;

      if (!clipboardData || clipboardData.length === 0) {
        try {
          const stored = localStorage.getItem('tfasim_field_clipboard');
          if (stored) {
            const parsed = JSON.parse(stored);
            // Only use if less than 24 hours old
            if (parsed.fields && Date.now() - parsed.timestamp < 24 * 60 * 60 * 1000) {
              clipboardData = parsed.fields;
            }
          }
        } catch (e) {
          // Ignore localStorage errors
        }
      }

      if (!clipboardData || clipboardData.length === 0) return state;

      const fields = [...state.formSchema.fields];
      const newFieldIds: string[] = [];

      // Find insert position (after selected field or at end)
      let insertIndex = fields.length;
      if (state.selectedFieldId) {
        const idx = fields.findIndex((f) => f.id === state.selectedFieldId);
        if (idx !== -1) insertIndex = idx + 1;
      }

      // Create new fields with new IDs
      const newFields = clipboardData.map((field: FormField) => {
        const newId = generateUUID();
        newFieldIds.push(newId);
        return {
          ...JSON.parse(JSON.stringify(field)),
          id: newId,
          name: `${field.name}_${Date.now()}`,
        };
      });

      fields.splice(insertIndex, 0, ...newFields);

      return {
        formSchema: { ...state.formSchema, fields },
        selectedFieldIds: newFieldIds,
        selectedFieldId: newFieldIds[0],
        hasUnsavedChanges: true,
        clipboard: clipboardData, // Update in-memory clipboard
      };
    }),

  addField: (field, afterFieldId) =>
    set((state) => {
      const fields = [...state.formSchema.fields];
      if (afterFieldId) {
        const index = fields.findIndex((f) => f.id === afterFieldId);
        if (index !== -1) {
          fields.splice(index + 1, 0, field);
        } else {
          // If afterFieldId doesn't exist, add to the end
          fields.push(field);
        }
      } else {
        fields.push(field);
      }
      return {
        formSchema: { ...state.formSchema, fields },
        selectedFieldId: field.id,
        hasUnsavedChanges: true,
      };
    }),

  updateField: (fieldId, updates) =>
    set((state) => {
      const fields = state.formSchema.fields.map((f) => {
        // Update field if it's in main canvas
        if (f.id === fieldId) {
          return { ...f, ...updates };
        }

        // Update field if it's inside a layout column
        if (f.type === 'layout' && f.properties.columnFields) {
          const columnFields = f.properties.columnFields.map((column) =>
            column.map((cf) => (cf.id === fieldId ? { ...cf, ...updates } : cf))
          );

          // Only update if we actually found and modified a field
          if (columnFields.some((col, idx) => col !== f.properties.columnFields![idx])) {
            return {
              ...f,
              properties: {
                ...f.properties,
                columnFields,
              },
            };
          }
        }

        // Update field if it's inside a repeatable column
        if (f.type === 'repeatable' && f.properties.columnFields) {
          const columnFields = f.properties.columnFields.map((column) =>
            column.map((cf) => (cf.id === fieldId ? { ...cf, ...updates } : cf))
          );

          // Only update if we actually found and modified a field
          if (columnFields.some((col, idx) => col !== f.properties.columnFields![idx])) {
            return {
              ...f,
              properties: {
                ...f.properties,
                columnFields,
              },
            };
          }
        }

        return f;
      });

      return {
        formSchema: { ...state.formSchema, fields },
        hasUnsavedChanges: true,
      };
    }),

  removeField: (fieldId) =>
    set((state) => {
      // Remove from main fields array
      let fields = state.formSchema.fields.filter((f) => f.id !== fieldId);

      // Also remove from any layout column that contains it
      fields = fields.map((f) => {
        if (f.type === 'layout' && f.properties.columnFields) {
          const columnFields = f.properties.columnFields.map((column) =>
            column.filter((cf) => cf.id !== fieldId)
          );
          return {
            ...f,
            properties: {
              ...f.properties,
              columnFields,
            },
          };
        }
        return f;
      });

      const conditionalLogic = state.formSchema.conditionalLogic.filter(
        (r) => r.targetFieldId !== fieldId && !r.conditions.some((c) => c.fieldId === fieldId)
      );
      return {
        formSchema: { ...state.formSchema, fields, conditionalLogic },
        selectedFieldId: state.selectedFieldId === fieldId ? null : state.selectedFieldId,
        hasUnsavedChanges: true,
      };
    }),

  reorderFields: (activeId, overId) =>
    set((state) => {
      const fields = state.formSchema.fields;
      const activeIndex = fields.findIndex((f) => f.id === activeId);
      const overIndex = fields.findIndex((f) => f.id === overId);

      if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) {
        console.warn('reorderFields: Invalid operation', {
          activeId,
          overId,
          activeIndex,
          overIndex,
          reason:
            activeIndex === -1
              ? 'Active field not found'
              : overIndex === -1
              ? 'Target field not found'
              : 'Same field',
        });
        return state;
      }

      return {
        formSchema: { ...state.formSchema, fields: arrayMove(fields, activeIndex, overIndex) },
        hasUnsavedChanges: true,
      };
    }),

  moveFieldUp: (fieldId) =>
    set((state) => {
      const fields = state.formSchema.fields;
      const index = fields.findIndex((f) => f.id === fieldId);

      if (index <= 0) {
        return state;
      }

      return {
        formSchema: { ...state.formSchema, fields: arrayMove(fields, index, index - 1) },
        hasUnsavedChanges: true,
      };
    }),

  moveFieldDown: (fieldId) =>
    set((state) => {
      const fields = state.formSchema.fields;
      const index = fields.findIndex((f) => f.id === fieldId);

      if (index === -1 || index >= fields.length - 1) {
        return state;
      }

      return {
        formSchema: { ...state.formSchema, fields: arrayMove(fields, index, index + 1) },
        hasUnsavedChanges: true,
      };
    }),

  duplicateField: (fieldId) =>
    set((state) => {
      const field = state.formSchema.fields.find((f) => f.id === fieldId);
      if (!field) return state;

      const newField: FormField = {
        ...JSON.parse(JSON.stringify(field)),
        id: generateUUID(),
        name: `${field.name}_copy`,
        label: `${field.label} (Copy)`,
      };

      const fields = [...state.formSchema.fields];
      const index = fields.findIndex((f) => f.id === fieldId);
      fields.splice(index + 1, 0, newField);

      return {
        formSchema: { ...state.formSchema, fields },
        selectedFieldId: newField.id,
        hasUnsavedChanges: true,
      };
    }),

  // Layout column operations
  addFieldToLayoutColumn: (layoutFieldId, columnIndex, field, afterFieldId) =>
    set((state) => {
      const fields = state.formSchema.fields.map((f) => {
        if (f.id === layoutFieldId && f.type === 'layout') {
          const columnFields = [...(f.properties.columnFields || [])];
          while (columnFields.length <= columnIndex) {
            columnFields.push([]);
          }
          const column = [...columnFields[columnIndex]];

          if (afterFieldId) {
            const index = column.findIndex((cf) => cf.id === afterFieldId);
            if (index !== -1) {
              column.splice(index + 1, 0, field);
            } else {
              column.push(field);
            }
          } else {
            column.push(field);
          }

          columnFields[columnIndex] = column;

          return {
            ...f,
            properties: {
              ...f.properties,
              columnFields,
            },
          };
        }
        return f;
      });

      return {
        formSchema: { ...state.formSchema, fields },
        selectedFieldId: field.id,
        hasUnsavedChanges: true,
      };
    }),

  removeFieldFromLayoutColumn: (layoutFieldId, columnIndex, fieldId) =>
    set((state) => {
      const fields = state.formSchema.fields.map((f) => {
        if (f.id === layoutFieldId && f.type === 'layout') {
          const columnFields = [...(f.properties.columnFields || [])];
          if (columnFields[columnIndex]) {
            columnFields[columnIndex] = columnFields[columnIndex].filter((cf) => cf.id !== fieldId);
          }

          return {
            ...f,
            properties: {
              ...f.properties,
              columnFields,
            },
          };
        }
        return f;
      });

      // Clean up conditional logic rules that reference this field
      const conditionalLogic = state.formSchema.conditionalLogic.filter(
        (r) => r.targetFieldId !== fieldId && !r.conditions.some((c) => c.fieldId === fieldId)
      );

      return {
        formSchema: { ...state.formSchema, fields, conditionalLogic },
        selectedFieldId: state.selectedFieldId === fieldId ? null : state.selectedFieldId,
        hasUnsavedChanges: true,
      };
    }),

  reorderFieldsInLayoutColumn: (layoutFieldId, columnIndex, activeId, overId) =>
    set((state) => {
      const fields = state.formSchema.fields.map((f) => {
        if (f.id === layoutFieldId && f.type === 'layout') {
          const columnFields = [...(f.properties.columnFields || [])];
          const column = columnFields[columnIndex] || [];
          const activeIndex = column.findIndex((cf) => cf.id === activeId);
          const overIndex = column.findIndex((cf) => cf.id === overId);

          if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) {
            console.warn('reorderFieldsInLayoutColumn: Invalid operation', {
              layoutFieldId,
              columnIndex,
              activeId,
              overId,
              activeIndex,
              overIndex,
              reason:
                activeIndex === -1
                  ? 'Active field not found in column'
                  : overIndex === -1
                  ? 'Target field not found in column'
                  : 'Same field',
            });
            return f;
          }

          columnFields[columnIndex] = arrayMove(column, activeIndex, overIndex);

          return {
            ...f,
            properties: {
              ...f.properties,
              columnFields,
            },
          };
        }
        return f;
      });

      return {
        formSchema: { ...state.formSchema, fields },
        hasUnsavedChanges: true,
      };
    }),

  moveFieldFromCanvasToLayoutColumn: (fieldId, layoutFieldId, columnIndex) =>
    set((state) => {
      // First, check if field is in main canvas
      let field = state.formSchema.fields.find((f) => f.id === fieldId);
      let isInCanvas = !!field;

      // If not in canvas, check if it's already in a layout column
      if (!field) {
        for (const layoutField of state.formSchema.fields) {
          if (layoutField.type === 'layout' && layoutField.properties.columnFields) {
            for (const column of layoutField.properties.columnFields) {
              const foundField = column.find((f) => f.id === fieldId);
              if (foundField) {
                field = foundField;
                break;
              }
            }
            if (field) break;
          }
        }
      }

      // If field not found anywhere, return
      if (!field) return state;

      // Remove from canvas if it's there
      let canvasFields = isInCanvas
        ? state.formSchema.fields.filter((f) => f.id !== fieldId)
        : [...state.formSchema.fields];

      // Remove from any layout columns and add to target column
      const fields = canvasFields.map((f) => {
        if (f.type === 'layout' && f.properties.columnFields) {
          const columnFields = f.properties.columnFields.map((column) =>
            column.filter((colField) => colField.id !== fieldId)
          );

          // If this is the target layout, add field to target column
          if (f.id === layoutFieldId) {
            while (columnFields.length <= columnIndex) {
              columnFields.push([]);
            }
            columnFields[columnIndex] = [...columnFields[columnIndex], field];
          }

          return {
            ...f,
            properties: {
              ...f.properties,
              columnFields,
            },
          };
        }
        return f;
      });

      return {
        formSchema: { ...state.formSchema, fields },
        selectedFieldId: field.id,
        hasUnsavedChanges: true,
      };
    }),

  moveFieldBetweenLayoutColumns: (
    fieldId,
    sourceLayoutId,
    sourceColumnIndex,
    targetLayoutId,
    targetColumnIndex
  ) =>
    set((state) => {
      // Find the field in the source column
      let fieldToMove: FormField | null = null;
      for (const layoutField of state.formSchema.fields) {
        if (
          layoutField.type === 'layout' &&
          layoutField.id === sourceLayoutId &&
          layoutField.properties.columnFields
        ) {
          const sourceColumn = layoutField.properties.columnFields[sourceColumnIndex];
          if (sourceColumn) {
            fieldToMove = sourceColumn.find((f) => f.id === fieldId) || null;
            if (fieldToMove) break;
          }
        }
      }

      if (!fieldToMove) {
        console.warn('moveFieldBetweenLayoutColumns: Field not found', {
          fieldId,
          sourceLayoutId,
          sourceColumnIndex,
        });
        return state;
      }

      // Create a deep copy to avoid stale references
      const fieldCopy = JSON.parse(JSON.stringify(fieldToMove)) as FormField;

      // Update all layout fields atomically
      const fields = state.formSchema.fields.map((f) => {
        if (f.type !== 'layout' || !f.properties.columnFields) {
          return f;
        }

        const columnFields = [...f.properties.columnFields];

        // Remove from source column
        if (f.id === sourceLayoutId) {
          if (columnFields[sourceColumnIndex]) {
            columnFields[sourceColumnIndex] = columnFields[sourceColumnIndex].filter(
              (cf) => cf.id !== fieldId
            );
          }
        }

        // Add to target column
        if (f.id === targetLayoutId) {
          while (columnFields.length <= targetColumnIndex) {
            columnFields.push([]);
          }
          columnFields[targetColumnIndex] = [...columnFields[targetColumnIndex], fieldCopy];
        }

        return {
          ...f,
          properties: {
            ...f.properties,
            columnFields,
          },
        };
      });

      return {
        formSchema: { ...state.formSchema, fields },
        hasUnsavedChanges: true,
      };
    }),

  // Repeatable column operations (mirror of layout column operations)
  addFieldToRepeatableColumn: (repeatableFieldId, columnIndex, field, afterFieldId) =>
    set((state) => {
      const fields = state.formSchema.fields.map((f) => {
        if (f.id === repeatableFieldId && f.type === 'repeatable') {
          const columnFields = [...(f.properties.columnFields || [[]])];
          while (columnFields.length <= columnIndex) {
            columnFields.push([]);
          }
          const column = [...columnFields[columnIndex]];

          if (afterFieldId) {
            const index = column.findIndex((cf) => cf.id === afterFieldId);
            if (index !== -1) {
              column.splice(index + 1, 0, field);
            } else {
              column.push(field);
            }
          } else {
            column.push(field);
          }

          columnFields[columnIndex] = column;

          return {
            ...f,
            properties: {
              ...f.properties,
              columnFields,
            },
          };
        }
        return f;
      });

      return {
        formSchema: { ...state.formSchema, fields },
        hasUnsavedChanges: true,
      };
    }),

  removeFieldFromRepeatableColumn: (repeatableFieldId, columnIndex, fieldId) =>
    set((state) => {
      const fields = state.formSchema.fields.map((f) => {
        if (f.id === repeatableFieldId && f.type === 'repeatable') {
          const columnFields = [...(f.properties.columnFields || [[]])];
          if (columnFields[columnIndex]) {
            columnFields[columnIndex] = columnFields[columnIndex].filter((cf) => cf.id !== fieldId);
          }
          return {
            ...f,
            properties: {
              ...f.properties,
              columnFields,
            },
          };
        }
        return f;
      });

      const conditionalLogic = state.formSchema.conditionalLogic.filter(
        (r) => r.targetFieldId !== fieldId && !r.conditions.some((c) => c.fieldId === fieldId)
      );

      return {
        formSchema: { ...state.formSchema, fields, conditionalLogic },
        selectedFieldId: state.selectedFieldId === fieldId ? null : state.selectedFieldId,
        hasUnsavedChanges: true,
      };
    }),

  reorderFieldsInRepeatableColumn: (repeatableFieldId, columnIndex, activeId, overId) =>
    set((state) => {
      const fields = state.formSchema.fields.map((f) => {
        if (f.id === repeatableFieldId && f.type === 'repeatable') {
          const columnFields = [...(f.properties.columnFields || [[]])];
          const column = columnFields[columnIndex] || [];
          const activeIndex = column.findIndex((cf) => cf.id === activeId);
          const overIndex = column.findIndex((cf) => cf.id === overId);

          if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) {
            console.warn('reorderFieldsInRepeatableColumn: Invalid operation', {
              repeatableFieldId,
              columnIndex,
              activeId,
              overId,
              activeIndex,
              overIndex,
            });
            return f;
          }

          columnFields[columnIndex] = arrayMove(column, activeIndex, overIndex);

          return {
            ...f,
            properties: {
              ...f.properties,
              columnFields,
            },
          };
        }
        return f;
      });

      return {
        formSchema: { ...state.formSchema, fields },
        hasUnsavedChanges: true,
      };
    }),

  moveFieldFromCanvasToRepeatableColumn: (fieldId, repeatableFieldId, columnIndex) =>
    set((state) => {
      let field = state.formSchema.fields.find((f) => f.id === fieldId);
      let isInCanvas = !!field;

      if (!field) {
        for (const repeatableField of state.formSchema.fields) {
          if (repeatableField.type === 'repeatable' && repeatableField.properties.columnFields) {
            for (const column of repeatableField.properties.columnFields) {
              const foundField = column.find((f) => f.id === fieldId);
              if (foundField) {
                field = foundField;
                break;
              }
            }
            if (field) break;
          }
        }
      }

      if (!field) return state;

      let canvasFields = isInCanvas
        ? state.formSchema.fields.filter((f) => f.id !== fieldId)
        : [...state.formSchema.fields];

      const fields = canvasFields.map((f) => {
        if (f.type === 'repeatable' && f.properties.columnFields) {
          const columnFields = f.properties.columnFields.map((column) =>
            column.filter((colField) => colField.id !== fieldId)
          );

          if (f.id === repeatableFieldId) {
            while (columnFields.length <= columnIndex) {
              columnFields.push([]);
            }
            columnFields[columnIndex] = [...columnFields[columnIndex], field];
          }

          return {
            ...f,
            properties: {
              ...f.properties,
              columnFields,
            },
          };
        }
        return f;
      });

      return {
        formSchema: { ...state.formSchema, fields },
        selectedFieldId: field.id,
        hasUnsavedChanges: true,
      };
    }),

  moveFieldBetweenRepeatableColumns: (
    fieldId,
    sourceRepeatableId,
    sourceColumnIndex,
    targetRepeatableId,
    targetColumnIndex
  ) =>
    set((state) => {
      let fieldToMove: FormField | null = null;
      for (const repeatableField of state.formSchema.fields) {
        if (
          repeatableField.type === 'repeatable' &&
          repeatableField.id === sourceRepeatableId &&
          repeatableField.properties.columnFields
        ) {
          const sourceColumn = repeatableField.properties.columnFields[sourceColumnIndex];
          if (sourceColumn) {
            fieldToMove = sourceColumn.find((f) => f.id === fieldId) || null;
            if (fieldToMove) break;
          }
        }
      }

      if (!fieldToMove) {
        console.warn('moveFieldBetweenRepeatableColumns: Field not found', {
          fieldId,
          sourceRepeatableId,
          sourceColumnIndex,
        });
        return state;
      }

      const fieldCopy = JSON.parse(JSON.stringify(fieldToMove)) as FormField;

      const fields = state.formSchema.fields.map((f) => {
        if (f.type !== 'repeatable' || !f.properties.columnFields) {
          return f;
        }

        const columnFields = [...f.properties.columnFields];

        if (f.id === sourceRepeatableId) {
          if (columnFields[sourceColumnIndex]) {
            columnFields[sourceColumnIndex] = columnFields[sourceColumnIndex].filter(
              (cf) => cf.id !== fieldId
            );
          }
        }

        if (f.id === targetRepeatableId) {
          while (columnFields.length <= targetColumnIndex) {
            columnFields.push([]);
          }
          columnFields[targetColumnIndex] = [...columnFields[targetColumnIndex], fieldCopy];
        }

        return {
          ...f,
          properties: {
            ...f.properties,
            columnFields,
          },
        };
      });

      return {
        formSchema: { ...state.formSchema, fields },
        hasUnsavedChanges: true,
      };
    }),

  addStep: (step) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        steps: [...(state.formSchema.steps || []), step],
      },
      activeStepId: step.id,
      hasUnsavedChanges: true,
    })),

  updateStep: (stepId, updates) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        steps: (state.formSchema.steps || []).map((s) =>
          s.id === stepId ? { ...s, ...updates } : s
        ),
      },
      hasUnsavedChanges: true,
    })),

  removeStep: (stepId) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        steps: (state.formSchema.steps || []).filter((s) => s.id !== stepId),
      },
      activeStepId: state.activeStepId === stepId ? null : state.activeStepId,
      hasUnsavedChanges: true,
    })),

  reorderSteps: (activeId, overId) =>
    set((state) => {
      const steps = state.formSchema.steps || [];
      const activeIndex = steps.findIndex((s) => s.id === activeId);
      const overIndex = steps.findIndex((s) => s.id === overId);

      if (activeIndex === -1 || overIndex === -1 || activeIndex === overIndex) {
        return state;
      }

      return {
        formSchema: { ...state.formSchema, steps: arrayMove(steps, activeIndex, overIndex) },
        hasUnsavedChanges: true,
      };
    }),

  moveFieldToStep: (fieldId, stepId) =>
    set((state) => {
      const steps = (state.formSchema.steps || []).map((step) => ({
        ...step,
        fieldIds: step.fieldIds.filter((id) => id !== fieldId),
      }));

      const stepIndex = steps.findIndex((s) => s.id === stepId);
      if (stepIndex !== -1) {
        steps[stepIndex].fieldIds.push(fieldId);
      }

      return {
        formSchema: { ...state.formSchema, steps },
        hasUnsavedChanges: true,
      };
    }),

  addCondition: (rule) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        conditionalLogic: [...state.formSchema.conditionalLogic, rule],
      },
      hasUnsavedChanges: true,
    })),

  updateCondition: (ruleId, updates) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        conditionalLogic: state.formSchema.conditionalLogic.map((r) =>
          r.id === ruleId ? { ...r, ...updates } : r
        ),
      },
      hasUnsavedChanges: true,
    })),

  removeCondition: (ruleId) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        conditionalLogic: state.formSchema.conditionalLogic.filter((r) => r.id !== ruleId),
      },
      hasUnsavedChanges: true,
    })),

  updateConditionalLogic: (rules) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        conditionalLogic: rules,
      },
      hasUnsavedChanges: true,
    })),

  updateSettings: (settings) =>
    set((state) => ({
      formSchema: {
        ...state.formSchema,
        settings: { ...state.formSchema.settings, ...settings },
      },
      hasUnsavedChanges: true,
    })),

  updateQuizQuestions: (questions: Array<{
    id: string;
    question: string;
    options: Array<{ id: string; text: string; isCorrect: boolean }>;
    points: number;
  }>) =>
    set((state) => {
      const fields: FormField[] = questions.map((q, idx) => ({
        id: q.id,
        type: 'radio' as const,
        label: q.question,
        name: `question_${idx + 1}`,
        required: true,
        options: q.options.map(opt => ({
          label: opt.text,
          value: opt.id,
        })),
        properties: {
          correctAnswer: q.options.filter(o => o.isCorrect).map(o => o.id),
          points: q.points,
        },
      }));

      return {
        formSchema: { ...state.formSchema, fields },
        hasUnsavedChanges: true,
      };
    }),

  setDragging: (isDragging) => set({ isDragging }),

  getSchema: () => get().formSchema,

  markAsSaved: () =>
    set((state) => ({
      originalSchema: JSON.parse(JSON.stringify(state.formSchema)),
      hasUnsavedChanges: false,
    })),
}),
    {
      // Only track changes to formSchema for undo/redo
      partialize: (state) => ({
        formSchema: state.formSchema,
      }),
      // Limit history to 50 items
      limit: 50,
      // Don't track rapid changes (debounce)
      handleSet: (handleSet) => {
        return (state) => {
          handleSet(state);
        };
      },
    }
  )
);
